import { useTranslation } from "react-i18next";
import React, { useCallback } from "react";
import { FaAnglesLeft, FaAnglesRight } from "react-icons/fa6";
import i18n from "@/i18n";

interface PaginationControlsProps {
  pageNum: number;
  setPageNum: (value: number) => void;
  totalItems: number | null;
  currentPage: number;
  setCurrentPage: (value: number) => void;
  totalPages: number;
}

const PaginationControls: React.FC<PaginationControlsProps> = ({
  pageNum,
  setPageNum,
  totalItems,
  currentPage,
  setCurrentPage,
  totalPages,
}) => {
  const { t } = useTranslation();

  const handleClick = useCallback(
    (page: number) => {
      if (page >= 1 && page <= totalPages) {
        setCurrentPage(page);
      }
    },
    [totalPages, setCurrentPage]
  );

  const getPageNumbers = useCallback(() => {
    const pages: (number | string)[] = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    pages.push(1);

    if (currentPage > 3) {
      pages.push("...");
    }

    const startPage = Math.max(2, currentPage - 1);
    const endPage = Math.min(totalPages - 1, currentPage + 1);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    if (currentPage < totalPages - 2) {
      pages.push("...");
    }

    pages.push(totalPages);

    // ✅ Remove duplicate numbers (but keep ellipses)
    const seen = new Set();
    return pages.filter((page) => {
      if (typeof page === "string") return true;
      if (seen.has(page)) return false;
      seen.add(page);
      return true;
    });
  }, [currentPage, totalPages]);
 

  return (
    <div className="flex flex-col md:flex-row gap-2 sm:flex-col items-center border-t border-gray-200 bg-white px-4 py-3 sm:px-6 justify-between w-full">
      <div>
        <p className="text-sm text-gray-700 flex items-center gap-5">
          <span>
            {t("paginationControl.show")}
            <span className="font-medium text-sidebar px-2">
              <select
                value={pageNum}
                onChange={(e) => {
                  setCurrentPage(1); // Reset to first page when changing items per page
                  setPageNum(Number(e.target.value)) }}
                className="border rounded-md text-center"
              >
                {[10, 20, 30, 40, 50].map((num) => (
                  <option key={num} className="block" value={num}>
                    {num}
                  </option>
                ))}
              </select>
            </span>
          </span>

          <span>
            {t("paginationControl.results")}
            <span className="font-medium text-sidebar px-2">
              {totalItems ?? ""}
            </span>
            {t("paginationControl.orders")}
          </span>
        </p>
      </div>

      {/* Mobile Navigation */}
      <div className="flex flex-1 justify-between sm:hidden">
        <button
          onClick={() => handleClick(currentPage - 1)}
          className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          disabled={currentPage === 1}
        >
          {t("paginationControl.previous")}
        </button>
        <button
          onClick={() => handleClick(currentPage + 1)}
          className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          disabled={currentPage === totalPages}
        >
          {t("paginationControl.next")}
        </button>
      </div>

      {/* Desktop Navigation */}
      <div className="hidden sm:flex sm:flex-1 md:flex-none sm:items-center">
        <nav
          aria-label="Pagination"
          className="isolate inline-flex -space-x-px rounded-md shadow-sm"
        >
          {/* Previous Button */}
          <button
            onClick={() => handleClick(currentPage - 1)}
            className="relative inline-flex items-center px-2 py-2 disabled:cursor-not-allowed text-gray-400 ring-1 bg-white ring-gray-300 hover:bg-gray-50 z-20"
            disabled={currentPage === 1}
          >
            <span className="sr-only">Previous</span>
            {i18n.dir() === "rtl" ? <FaAnglesRight /> : <FaAnglesLeft />}
          </button>

          {/* Page Numbers */}
          {getPageNumbers().map((page, index) =>
            page === "..." ? (
              <span
                key={`ellipsis-${index}`}
                className="px-4 py-2 text-sm font-semibold text-gray-500"
              >
                ...
              </span>
            ) : (
              <a
                key={`page-${page}-${index}`}  // Ensures uniqueness
                onClick={() => handleClick(page as number)}
                className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold cursor-pointer ${page === currentPage
                  ? "bg-sidebar text-white"
                  : "text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20"
                  }`}
              >
                {page}
              </a>
            )
          )}

          {/* Next Button */}
          <button
            onClick={() => handleClick(currentPage + 1)}
            className="relative inline-flex items-center px-2 py-2 disabled:cursor-not-allowed text-gray-400 ring-1 ring-gray-300 hover:bg-gray-50 bg-white z-20"
            disabled={currentPage === totalPages}
          >
            <span className="sr-only">Next</span>
            {i18n.dir() === "rtl" ? <FaAnglesLeft /> : <FaAnglesRight />}
          </button>
        </nav>
      </div>
    </div>
  );
};

export default PaginationControls;
